/**
 * Types para o módulo ProductImageRecolor
 * Reutilização de padrões do backend e frontend
 */

// ============================================================================
// TIPOS DE RESPOSTA DO BACKEND
// ============================================================================

/**
 * Imagem de Produto (tabela productImages)
 * Backend retorna: path + imgsuffix separados
 */
export interface ProductImage {
  id: number
  productId: number
  pluginspaceId: number
  typeImg: string // 'imgsmall', 'imglarge', 'image'
  imgsuffix: string // Nome chave: 'product-123.jpg'
  path: string // Caminho base S3: 'https://s3.amazonaws.com/...'
  order?: number
}

/**
 * Variação SKU (tabela skuLocalVariation)
 * Backend retorna: img é a URL COMPLETA já
 */
export interface SkuLocalVariation {
  id: number
  productId: number
  sku: string
  img: string // URL COMPLETA já, não precisa montar
  status: boolean
  pluginspaceId: number
}

/**
 * Produto para listagem/busca
 */
export interface ProductForSearch {
  id: number
  code: string
  name: string
  status?: boolean
  classification?: {
    id: number
    name: string
  }
}

/**
 * Item combinado de imagem (produto + variação)
 * Usado para exibir tudo junto na galeria
 */
export interface CombinedImageItem {
  id: string // Único entre produto e variação
  productId: number
  type: 'product-image' | 'sku-variation'
  imageUrl: string // URL completa já montada
  label: string // "Product Image" ou "SKU Variation"
  sku?: string // Apenas se for SKU
  sourceData: ProductImage | SkuLocalVariation
}

// ============================================================================
// TIPOS PARA ANÁLISE DE RECOLOR
// ============================================================================

/**
 * Request para análise de recolor (CAS-Service)
 */
export interface AnalyzeRecolorRequest {
  imageUrl: string
  objectDescription: string // Ex: "blue shirt"
  targetColor: string // Ex: "red"
}

/**
 * Response da análise de recolor
 */
export interface AnalyzeRecolorResponse {
  success: boolean
  data: {
    bounds: {
      x: number
      y: number
      width: number
      height: number
    }
    confidence: number
    description?: string
    analysis: string
    suggestedColors: string
  }
  timestamp: string
}

/**
 * Request para salvar imagem recolorida
 */
export interface SaveRecolorRequest {
  imageBase64: string
  subdomain: string // Ex: 'produtos'
  typefolder: string // Ex: 'camisas'
  typeimage: string // Ex: 'recolor'
  imageId?: string
  appliedColor?: string
}

/**
 * Response após salvar recolor
 */
export interface SaveRecolorResponse {
  success: boolean
  data: {
    url: string // URL da imagem salva
  }
  timestamp: string
}

// ============================================================================
// TIPOS DE ESTADO DO COMPONENTE
// ============================================================================

/**
 * Estado da tela de seleção de produto
 */
export interface ProductFilterState {
  searchTerm: string
  products: ProductForSearch[]
  selectedProduct: ProductForSearch | null
  loading: boolean
  error: string | null
}

/**
 * Estado da tela de detalhes (imagens + recolor)
 */
export interface ImageGalleryState {
  productId: number
  productImages: ProductImage[]
  skuVariations: SkuLocalVariation[]
  combinedImages: CombinedImageItem[]
  selectedImage: CombinedImageItem | null
  loading: boolean
  error: string | null
}

/**
 * Estado do recolor modal
 */
export interface RecolorModalState {
  visible: boolean
  selectedImage: CombinedImageItem | null
  selectedColor: string // Hex color
  analysis: AnalyzeRecolorResponse['data'] | null
  savingResult: SaveRecolorResponse['data'] | null
  loading: boolean
  error: string | null
  step: 'select-color' | 'analyzing' | 'analyzed' | 'saving' | 'saved'
}

// ============================================================================
// ENUMS
// ============================================================================

export enum RecolorModalStep {
  SELECT_COLOR = 'select-color',
  ANALYZING = 'analyzing',
  ANALYZED = 'analyzed',
  SAVING = 'saving',
  SAVED = 'saved',
}
